/** @jsxImportSource @emotion/react */
import { Buttons, TextFields, ThemeProvider } from '@zip/business-components';
import { SelectChangeEvent } from '@mui/material/Select';
import moment from 'moment';
import * as Icons from '@zip/icons';
import { AddressLookupInline, GuidedFileUploader } from 'components';
import { NavButtons } from 'components/sign-up';
import { useMerchantApplicationContext, useSnackbar } from 'contexts';
import { PageAction, PageRoute, State } from 'enums';
import { css, theme } from 'global';
import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { GuidedFileUploaderRef } from 'components/guided-file-upload';
import { isAbortError } from 'types/common/ApiHelpers';
import { StepComponentProps } from 'types/interfaces/sign-up/MerchantApplication.types';
import {
  ActivationProgressStatus,
  AddressResponse,
  DirectorDetailsResponse,
  IdentityDocumentType,
} from 'types/responses/activation';
import {
  DriversLicenceSuggestionsResponse,
  logEvent,
  PassportSuggestionsResponse,
  removeWhitespace,
  useActivationFunctions,
} from 'utils';
import { getStoredActivationData } from 'modules/activation/activationStorage';
import { VerifyIdIllustration } from 'public/illustrations';
import * as styles from './DirectorDetailsStep.styles';
import { renderFieldError } from 'utils/validators';
import { InlineAddress } from 'types';

type DirectorUploadStage =
  | 'select-id-type'
  | 'upload'
  | 'confirm-loading'
  | 'confirm-form';

type DirectorUploadContent = {
  title: string;
  requirements: string[];
  infoMessage: string;
  multiple: boolean;
  identityDocumentType: IdentityDocumentType;
};

type DirectorConfirmFormValues = {
  state: string;
  country: string;
  licenceNumber: string;
  licenceCardNumber: string;
  passportNumber: string;
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string;
  residentialAddress: string;
  contactEmail: string;
  contactMobile: string;
  identityDocumentStorage?: string[];
};

type ConfirmFormErrors = Partial<
  Record<keyof DirectorConfirmFormValues, string>
>;

const getConfirmFormErrors = (
  values: DirectorConfirmFormValues,
  identityDocumentType: IdentityDocumentType
): ConfirmFormErrors => {
  const errors: ConfirmFormErrors = {};

  if (identityDocumentType === IdentityDocumentType.DriversLicence) {
    if (!values.state) {
      errors.state = 'Please select an option';
    }
    if (!values.licenceNumber) {
      errors.licenceNumber = 'Please enter licence number';
    }
    if (!values.licenceCardNumber) {
      errors.licenceCardNumber = 'Please enter licence card number';
    }
  } else {
    if (!values.country) {
      errors.country = 'Please select an option';
    }
    if (!values.passportNumber) {
      errors.passportNumber = 'Please enter passport number';
    }
  }

  // Common validations
  if (!values.firstName) {
    errors.firstName = 'Please enter first name';
  }
  if (!values.lastName) {
    errors.lastName = 'Please enter last name';
  }
  if (!values.dateOfBirth) {
    errors.dateOfBirth = 'Please enter date of birth';
  }
  if (!values.residentialAddress) {
    errors.residentialAddress = 'Please enter residential address';
  }
  if (!values.contactMobile) {
    errors.contactMobile = 'Please enter mobile number';
  }
  if (!values.contactEmail) {
    errors.contactEmail = 'Please enter email';
  }

  return errors;
};

const MIN_VERIFICATION_DURATION_MS = 5000;
const SUGGESTIONS_TIMEOUT_MS = 5000;
const CONFIRM_DETAILS_LOADING_MS = 500;
const MAX_FILE_SIZE_BYTES = 8 * 1024 * 1024;
const MAX_DRIVERS_LICENCE_FILES = 2;
const PASSPORT_SINGLE_FILE_ERROR_MESSAGE =
  'You can only upload one passport file. Please delete and try again.';
const DRIVERS_LICENCE_MAX_FILES_ERROR_MESSAGE =
  'You can only upload two files. Please remove a file and try again.';
const ACCEPTED_FILE_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
]);
const AUSTRALIAN_STATE_OPTIONS = Object.values(State).map((value) => ({
  value,
  label: value,
}));

const DIRECTOR_UPLOAD_CONTENT: Partial<
  Record<IdentityDocumentType, DirectorUploadContent>
> = {
  [IdentityDocumentType.DriversLicence]: {
    title: "Upload driver's licence",
    requirements: [
      'Upload photo of the front of drivers licence',
      'Upload photo of the back of drivers licence',
      'PDF, JPEG, or PNG accepted - Max 8 MB each',
    ],
    infoMessage:
      'To proceed with your application, please upload the identification of a director or authorised representative.',
    multiple: true,
    identityDocumentType: IdentityDocumentType.DriversLicence,
  },
  [IdentityDocumentType.Passport]: {
    title: 'Upload passport',
    requirements: [
      'Upload photo or scan of passport page',
      'PDF, JPEG, or PNG accepted - Max 8 MB each',
    ],
    infoMessage:
      'To proceed with your application, please upload the identification of a director or authorised representative.',
    multiple: false,
    identityDocumentType: IdentityDocumentType.Passport,
  },
};

const isAcceptedFileType = (file: File): boolean => {
  if (ACCEPTED_FILE_TYPES.has(file.type)) {
    return true;
  }

  const lowerCaseFileName = file.name.toLowerCase();
  return (
    lowerCaseFileName.endsWith('.pdf') ||
    lowerCaseFileName.endsWith('.jpeg') ||
    lowerCaseFileName.endsWith('.jpg') ||
    lowerCaseFileName.endsWith('.png')
  );
};

const isValidUploadFile = (file: File): boolean =>
  isAcceptedFileType(file) && file.size <= MAX_FILE_SIZE_BYTES;

const areAllFilesValid = (files: File[]): boolean =>
  files.every(isValidUploadFile);

const isTruthyBoolean = (value: boolean | string | undefined): boolean =>
  value === true || String(value).toLowerCase() === 'true';

// MC-4775 Will not validate for now - To be re-introduced once confidence score is added in AI endpoint
const isValidPassportSuggestionsResponse = (
  value: PassportSuggestionsResponse
): boolean => true; //typeof value?.application_id === 'string';

// MC-4775 Will not validate for now - To be re-introduced once confidence score is added in AI endpoint
const isValidDriversLicenceSuggestionsResponse = (
  value: DriversLicenceSuggestionsResponse
): boolean => true; //typeof value?.application_id === 'string';

// MC-4775 Will not validate for now - To be re-introduced once confidence score is added in AI endpoint
const getDriversLicenceVerificationResults = (
  files: File[],
  suggestionsResponse?: DriversLicenceSuggestionsResponse | null
): boolean[] => [
  // Boolean(suggestionsResponse?.has_front),
  // Boolean(suggestionsResponse?.has_back),
  true,
  true,
  areAllFilesValid(files),
];

// MC-4775 Will not validate for now - To be re-introduced once confidence score is added in AI endpoint
const getPassportVerificationResults = (
  files: File[],
  suggestionsResponse?: PassportSuggestionsResponse | null
): boolean[] => [
  // Boolean(suggestionsResponse?.file_path),
  true,
  areAllFilesValid(files),
];

const uploadFileToS3 = async (
  url: string,
  file: File,
  signal: AbortSignal
): Promise<void> => {
  const res = await fetch(url, {
    method: 'PUT',
    body: file,
    signal,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to upload ${file.name} to S3`);
  }
};

const waitForDelay = (durationMs: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      signal.removeEventListener('abort', handleAbort);
      resolve();
    }, durationMs);

    const handleAbort = (): void => {
      window.clearTimeout(timeoutId);
      reject(new DOMException('The operation was aborted.', 'AbortError'));
    };

    signal.addEventListener('abort', handleAbort, { once: true });
  });

const waitForMinimumDuration = async (
  startedAtMs: number,
  minimumDurationMs: number,
  signal: AbortSignal
): Promise<void> => {
  const elapsedMs = Date.now() - startedAtMs;
  const remainingMs = minimumDurationMs - elapsedMs;

  if (remainingMs > 0) {
    await waitForDelay(remainingMs, signal);
  }
};

const withTimeout = async <T,>(
  promise: Promise<T>,
  durationMs: number,
  signal: AbortSignal,
  timeoutMessage: string
): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      signal.removeEventListener('abort', handleAbort);
      reject(new Error(timeoutMessage));
    }, durationMs);

    const handleAbort = (): void => {
      window.clearTimeout(timeoutId);
      reject(new DOMException('The operation was aborted.', 'AbortError'));
    };

    signal.addEventListener('abort', handleAbort, { once: true });

    promise.then(
      (value) => {
        window.clearTimeout(timeoutId);
        signal.removeEventListener('abort', handleAbort);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timeoutId);
        signal.removeEventListener('abort', handleAbort);
        reject(error);
      }
    );
  });

const getSelectOptions = (
  selectedIdentityDocumentType: IdentityDocumentType | null,
  currentValue: string,
  passportCountryOptions: { value: string; label: string }[]
): { value: string; label: string }[] => {
  const baseOptions =
    selectedIdentityDocumentType === IdentityDocumentType.DriversLicence
      ? AUSTRALIAN_STATE_OPTIONS
      : passportCountryOptions;

  if (!currentValue) {
    return baseOptions;
  }

  const hasMatch = baseOptions.some((option) => option.value === currentValue);

  return hasMatch
    ? baseOptions
    : [{ value: currentValue, label: currentValue }, ...baseOptions];
};

const getBaseIdentityConfirmFormValues = (suggestionsResponse: {
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  date_of_birth?: string | null;
  address?: string | null;
}) => ({
  firstName: suggestionsResponse.first_name ?? '',
  middleName: suggestionsResponse.middle_name ?? '',
  lastName: suggestionsResponse.last_name ?? '',
  dateOfBirth: suggestionsResponse.date_of_birth ?? '',
  residentialAddress: '',
  contactEmail: '',
  contactMobile: '',
});

const getDriversLicenceConfirmFormValues = (
  suggestionsResponse: DriversLicenceSuggestionsResponse
): DirectorConfirmFormValues => ({
  ...getBaseIdentityConfirmFormValues(suggestionsResponse),
  state: suggestionsResponse.state_or_country ?? '',
  country: '',
  licenceNumber: removeWhitespace(suggestionsResponse.licence_number ?? ''),
  licenceCardNumber: removeWhitespace(suggestionsResponse.card_number ?? ''),
  passportNumber: '',
});

const getPassportConfirmFormValues = (
  suggestionsResponse: PassportSuggestionsResponse
): DirectorConfirmFormValues => ({
  ...getBaseIdentityConfirmFormValues(suggestionsResponse),
  state: '',
  country: suggestionsResponse.country ?? '',
  licenceNumber: '',
  licenceCardNumber: '',
  passportNumber: removeWhitespace(suggestionsResponse.passport_number ?? ''),
});

const getEmptyConfirmFormValues = (): DirectorConfirmFormValues => ({
  state: '',
  country: '',
  licenceNumber: '',
  licenceCardNumber: '',
  passportNumber: '',
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  residentialAddress: '',
  contactEmail: '',
  contactMobile: '',
});

const formatAddress = (
  address:
    | {
        unitNumber?: string | null;
        streetNumber?: string | null;
        streetName?: string | null;
        suburb?: string | null;
        postcode?: string | null;
        state?: string | null;
      }
    | null
    | undefined
): string => {
  if (!address) {
    return '';
  }

  return [
    address.unitNumber
      ? `${address.unitNumber}/${address.streetNumber}`
      : address.streetNumber,
    address.streetName,
    address.suburb,
    [address.state, address.postcode].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');
};

const getInitialConfirmFormValues = (
  directorDetails: DirectorDetailsResponse | null | undefined
): DirectorConfirmFormValues => {
  if (!directorDetails?.firstName) {
    return getEmptyConfirmFormValues();
  }

  return {
    state: directorDetails.driversLicence?.state ?? '',
    country: directorDetails.passport?.country ?? '',
    licenceNumber: directorDetails.driversLicence?.licenceNumber ?? '',
    licenceCardNumber: directorDetails.driversLicence?.cardNumber ?? '',
    passportNumber: directorDetails.passport?.passportNumber ?? '',
    firstName: directorDetails.firstName ?? '',
    middleName: directorDetails.middleName ?? '',
    lastName: directorDetails.lastName ?? '',
    dateOfBirth: directorDetails.dateOfBirth ?? '',
    residentialAddress: formatAddress(directorDetails.residentialAddress),
    contactEmail: directorDetails.businessEmail ?? '',
    contactMobile: directorDetails.mobileNumber ?? '',
    identityDocumentStorage: directorDetails.identityDocumentStorage ?? [],
  };
};

const deriveIdentityDocumentType = (
  details: DirectorDetailsResponse | null | undefined
): IdentityDocumentType | null => {
  if (!details) {
    return null;
  }
  if (details.identityDocumentUsed === IdentityDocumentType.DriversLicence) {
    return IdentityDocumentType.DriversLicence;
  }
  if (details.identityDocumentUsed === IdentityDocumentType.Passport) {
    return IdentityDocumentType.Passport;
  }
  if (details.driversLicence?.licenceNumber || details.driversLicence?.state) {
    return IdentityDocumentType.DriversLicence;
  }
  if (details.passport?.passportNumber) {
    return IdentityDocumentType.Passport;
  }
  return null;
};

const buildDirectorDetailsPatchPayload = (
  values: DirectorConfirmFormValues,
  identityDocumentType: IdentityDocumentType,
  residentialAddress: AddressResponse | null
) => ({
  directorDetails: {
    identityDocumentUsed: identityDocumentType,
    identityDocumentStorage: values.identityDocumentStorage?.length
      ? values.identityDocumentStorage
      : null,
    firstName: values.firstName,
    middleName: values.middleName || null,
    lastName: values.lastName,
    businessEmail: values.contactEmail,
    mobileNumber: values.contactMobile,
    dateOfBirth: values.dateOfBirth || null,
    residentialAddress: residentialAddress ?? undefined,
    driversLicence:
      identityDocumentType === IdentityDocumentType.DriversLicence
        ? {
            state: values.state || null,
            licenceNumber: values.licenceNumber || null,
            cardNumber: values.licenceCardNumber || null,
          }
        : undefined,
    passport:
      identityDocumentType === IdentityDocumentType.Passport
        ? {
            country: values.country || null,
            passportNumber: values.passportNumber || null,
          }
        : undefined,
  },
});

type DirectorDetailsStepModuleProps = StepComponentProps & {
  stepProgress?: ActivationProgressStatus;
  onBackToBusinessDetails?: () => void;
  onRegisterCloseHandler?: (handler: (() => Promise<void>) | null) => void;
};

export const DirectorDetailsStepModule: FC<DirectorDetailsStepModuleProps> = ({
  onReturnToDashboard,
  stepProgress,
  onBackToBusinessDetails,
  onRegisterCloseHandler,
}) => {
  const navigate = useNavigate();
  const Snackbar = useSnackbar();
  const uploaderRef = useRef<GuidedFileUploaderRef>(null);
  const verificationRunIdRef = useRef(0);
  const verificationAbortControllerRef = useRef<AbortController | null>(null);
  const skipConfirmLoadingRef = useRef(false);
  const verificationCacheRef = useRef<{
    identityDocumentType: IdentityDocumentType;
    files: File[];
    results: boolean[];
    formValues: DirectorConfirmFormValues;
  } | null>(null);
  const loadedFilesidentityDocumentTypeRef =
    useRef<IdentityDocumentType | null>(null);
  const residentialAddressRef = useRef<AddressResponse | null>(null);
  const hasAppliedCachePrefillRef = useRef(false);
  const fetchForJumpRef = useRef(false);
  const {
    activationData,
    formOptions,
    applicationReferrer,
    merchantApplicationId,
    refreshActivationData,
  } = useMerchantApplicationContext();
  const isNdpReferrer = applicationReferrer?.toLowerCase() === 'ndp';
  const shouldStartAtConfirmFormRef = useRef(
    stepProgress === ActivationProgressStatus.Edit ||
      stepProgress === ActivationProgressStatus.Confirmed
  );
  const identityDocumentTypeAtMount = deriveIdentityDocumentType(
    activationData?.directorDetails
  );
  const startedAtConfirmFormRef = useRef(
    shouldStartAtConfirmFormRef.current && identityDocumentTypeAtMount !== null
  );
  const {
    abortRequests,
    getActivationData,
    getMerchantApplicationUploadUrlAsync,
    getDriversLicenceSuggestionsAsync,
    getPassportSuggestionsAsync,
    patchMerchantApplicationAsync,
    patchApplicationProgressAsync,
    submitMerchantApplicationAsync,
  } = useActivationFunctions();
  const [selectedIdentityDocumentType, setSelectedIdentityDocumentType] =
    useState<IdentityDocumentType | null>(() =>
      startedAtConfirmFormRef.current ? identityDocumentTypeAtMount : null
    );
  const [stage, setStage] = useState<DirectorUploadStage>(() =>
    startedAtConfirmFormRef.current ? 'confirm-form' : 'select-id-type'
  );
  const [loadedFiles, setLoadedFiles] = useState<File[]>([]);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null
  );
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSubmittingConfirmForm, setIsSubmittingConfirmForm] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [confirmFormValues, setConfirmFormValues] =
    useState<DirectorConfirmFormValues>(getEmptyConfirmFormValues);
  const [
    residentialAddressResolutionFailed,
    setResidentialAddressResolutionFailed,
  ] = useState(false);
  const [rawAISuggestedAddress, setRawAISuggestedAddress] = useState<
    string | null
  >(null);

  const selectedContent = selectedIdentityDocumentType
    ? DIRECTOR_UPLOAD_CONTENT[selectedIdentityDocumentType]
    : null;

  let continueLabel: string;
  let canContinue: boolean;

  switch (stage) {
    case 'select-id-type':
      continueLabel = 'Next';
      canContinue = Boolean(selectedIdentityDocumentType);
      break;
    case 'upload':
      continueLabel = 'Verify';
      canContinue = loadedFiles.length > 0 && !isVerifying;
      break;
    default:
      continueLabel = isNdpReferrer ? 'Submit' : 'Confirm and continue';
      canContinue = !isSubmittingConfirmForm;
  }

  const progressPercent = useMemo(() => {
    if (stage === 'confirm-loading' || stage === 'confirm-form') {
      return 60;
    }

    return 48;
  }, [stage]);

  const handleFilesChange = useCallback((files: File[]): void => {
    setUploadErrorMessage(null);
    setLoadedFiles((currentFiles) => {
      const hasSameFiles =
        currentFiles.length === files.length &&
        currentFiles.every((file, index) => file === files[index]);

      return hasSameFiles ? currentFiles : files;
    });
  }, []);

  const handlePassportSingleFileReplaceAttempt = useCallback((): void => {
    Snackbar.error(PASSPORT_SINGLE_FILE_ERROR_MESSAGE);
  }, [Snackbar]);

  const handleDriversLicenceMaxFilesAttempt = useCallback((): void => {
    Snackbar.error(DRIVERS_LICENCE_MAX_FILES_ERROR_MESSAGE);
  }, [Snackbar]);

  const abortVerification = useCallback((): void => {
    verificationRunIdRef.current += 1;
    setIsVerifying(false);
    abortRequests();
    verificationAbortControllerRef.current?.abort();
    verificationAbortControllerRef.current = null;
  }, [abortRequests]);

  const handleUploaderVerify = useCallback(
    async (files: File[]): Promise<boolean[]> => {
      logEvent(PageAction.merchantOnboardingUAUploadDirectorDocument, {
        merchantApplicationId,
        selectedIdentityDocumentType,
      });

      const selectedUploadContent =
        DIRECTOR_UPLOAD_CONTENT[
          selectedIdentityDocumentType ?? IdentityDocumentType.DriversLicence
        ];
      skipConfirmLoadingRef.current = false;
      if (
        selectedIdentityDocumentType === IdentityDocumentType.DriversLicence &&
        files.length > MAX_DRIVERS_LICENCE_FILES
      ) {
        Snackbar.error(DRIVERS_LICENCE_MAX_FILES_ERROR_MESSAGE);
        return getDriversLicenceVerificationResults(files, null);
      }
      if (!areAllFilesValid(files)) {
        return selectedIdentityDocumentType === IdentityDocumentType.Passport
          ? getPassportVerificationResults(files, null)
          : getDriversLicenceVerificationResults(files, null);
      }

      try {
        verificationAbortControllerRef.current = new AbortController();
        const { signal } = verificationAbortControllerRef.current;
        const verificationStartedAtMs = Date.now();

        const uploadedFilePaths: string[] = [];

        for (const file of files) {
          const uploadUrlResponse = await getMerchantApplicationUploadUrlAsync(
            selectedUploadContent.identityDocumentType,
            file.name
          );

          await uploadFileToS3(uploadUrlResponse.url, file, signal);

          const filePath = uploadUrlResponse.s3ObjectUri;
          uploadedFilePaths.push(filePath);
        }

        const identityDocumentStorage = uploadedFilePaths.map((p) =>
          p.replace(/^s3:\/\/[^/]+\//, '')
        );

        if (selectedIdentityDocumentType === IdentityDocumentType.Passport) {
          let suggestionsResponse: PassportSuggestionsResponse;

          try {
            suggestionsResponse = await withTimeout(
              getPassportSuggestionsAsync(uploadedFilePaths[0]),
              SUGGESTIONS_TIMEOUT_MS,
              signal,
              'Passport suggestions timed out'
            );
          } catch (error) {
            if (isAbortError(error)) {
              throw error;
            }

            await waitForMinimumDuration(
              verificationStartedAtMs,
              MIN_VERIFICATION_DURATION_MS,
              signal
            );

            console.error('Director ID suggestions failed', error);
            skipConfirmLoadingRef.current = true;
            setConfirmFormValues((prev) => ({
              ...getEmptyConfirmFormValues(),
              contactEmail: prev.contactEmail,
              contactMobile: prev.contactMobile,
              identityDocumentStorage,
            }));
            residentialAddressRef.current = null;
            setRawAISuggestedAddress(null);
            return selectedUploadContent.requirements.map(() => true);
          }

          if (!isValidPassportSuggestionsResponse(suggestionsResponse)) {
            await waitForMinimumDuration(
              verificationStartedAtMs,
              MIN_VERIFICATION_DURATION_MS,
              signal
            );
            skipConfirmLoadingRef.current = true;
            setConfirmFormValues((prev) => ({
              ...getEmptyConfirmFormValues(),
              contactEmail: prev.contactEmail,
              contactMobile: prev.contactMobile,
              identityDocumentStorage,
            }));
            residentialAddressRef.current = null;
            setRawAISuggestedAddress(null);
            return selectedUploadContent.requirements.map(() => true);
          }

          // MC-4775 Will not validate for now - To be re-introduced once confidence score is added in AI endpoint
          if (
            isTruthyBoolean(suggestionsResponse.is_country_blacklisted) &&
            false
          ) {
            await waitForMinimumDuration(
              verificationStartedAtMs,
              MIN_VERIFICATION_DURATION_MS,
              signal
            );
            setUploadErrorMessage(
              "Due to our verification requirements, passports issued by this country aren't currently supported."
            );
            return getPassportVerificationResults(files, null);
          }

          await waitForMinimumDuration(
            verificationStartedAtMs,
            MIN_VERIFICATION_DURATION_MS,
            signal
          );
          const passportFormValues =
            getPassportConfirmFormValues(suggestionsResponse);
          const passportResults = getPassportVerificationResults(
            files,
            suggestionsResponse
          );
          setConfirmFormValues((prev) => {
            const merged = {
              ...passportFormValues,
              contactEmail: prev.contactEmail,
              contactMobile: prev.contactMobile,
              identityDocumentStorage,
            };
            verificationCacheRef.current = {
              identityDocumentType: IdentityDocumentType.Passport,
              files,
              results: passportResults,
              formValues: merged,
            };
            return merged;
          });

          // Feeds raw AI suggested address to be resolved by Google Places API
          residentialAddressRef.current = null;
          setRawAISuggestedAddress(suggestionsResponse.address ?? null);
          return passportResults;
        }

        let suggestionsResponse: DriversLicenceSuggestionsResponse;

        try {
          suggestionsResponse = await withTimeout(
            getDriversLicenceSuggestionsAsync(uploadedFilePaths),
            SUGGESTIONS_TIMEOUT_MS,
            signal,
            'Driver licence suggestions timed out'
          );
        } catch (error) {
          if (isAbortError(error)) {
            throw error;
          }

          await waitForMinimumDuration(
            verificationStartedAtMs,
            MIN_VERIFICATION_DURATION_MS,
            signal
          );
          console.error('Director ID suggestions failed', error);
          skipConfirmLoadingRef.current = true;
          setConfirmFormValues((prev) => ({
            ...getEmptyConfirmFormValues(),
            contactEmail: prev.contactEmail,
            contactMobile: prev.contactMobile,
            identityDocumentStorage,
          }));
          residentialAddressRef.current = null;
          setRawAISuggestedAddress(null);
          return selectedUploadContent.requirements.map(() => true);
        }

        if (!isValidDriversLicenceSuggestionsResponse(suggestionsResponse)) {
          await waitForMinimumDuration(
            verificationStartedAtMs,
            MIN_VERIFICATION_DURATION_MS,
            signal
          );
          skipConfirmLoadingRef.current = true;
          setConfirmFormValues((prev) => ({
            ...getEmptyConfirmFormValues(),
            contactEmail: prev.contactEmail,
            contactMobile: prev.contactMobile,
            identityDocumentStorage,
          }));
          residentialAddressRef.current = null;
          setRawAISuggestedAddress(null);
          return selectedUploadContent.requirements.map(() => true);
        }

        if (suggestionsResponse.dl_digital) {
          await waitForMinimumDuration(
            verificationStartedAtMs,
            MIN_VERIFICATION_DURATION_MS,
            signal
          );
          setUploadErrorMessage(
            'There was an error when verifying your ID. Please check the requirements above and try again.'
          );
          return getDriversLicenceVerificationResults(files, null);
        }

        await waitForMinimumDuration(
          verificationStartedAtMs,
          MIN_VERIFICATION_DURATION_MS,
          signal
        );
        const driversLicenceFormValues =
          getDriversLicenceConfirmFormValues(suggestionsResponse);
        const driversLicenceResults = getDriversLicenceVerificationResults(
          files,
          suggestionsResponse
        );
        setConfirmFormValues((prev) => {
          const merged = {
            ...driversLicenceFormValues,
            contactEmail: prev.contactEmail,
            contactMobile: prev.contactMobile,
            identityDocumentStorage,
          };
          verificationCacheRef.current = {
            identityDocumentType: IdentityDocumentType.DriversLicence,
            files,
            results: driversLicenceResults,
            formValues: merged,
          };
          return merged;
        });

        // Feeds raw AI suggested address to be resolved by Google Places API
        residentialAddressRef.current = null;
        setRawAISuggestedAddress(suggestionsResponse.address ?? null);
        return driversLicenceResults;
      } catch (error) {
        if (isAbortError(error)) {
          throw error;
        }

        Snackbar.error('Upload failed. Please try again.');
        return selectedUploadContent.requirements.map(() => false);
      }
    },
    [
      getDriversLicenceSuggestionsAsync,
      getMerchantApplicationUploadUrlAsync,
      getPassportSuggestionsAsync,
      selectedIdentityDocumentType,
      Snackbar,
    ]
  );

  const handleBack = useCallback((): void => {
    abortVerification();

    if (stage === 'select-id-type') {
      if (onBackToBusinessDetails) {
        onBackToBusinessDetails();
      } else {
        navigate(PageRoute.Application);
      }
      return;
    }

    if (stage === 'upload') {
      verificationCacheRef.current = null;
      setStage('select-id-type');
      return;
    }

    if (stage === 'confirm-form') {
      void patchApplicationProgressAsync({
        directorDetails: ActivationProgressStatus.Upload,
      });
      setStage('upload');
      return;
    }

    navigate(PageRoute.Application);
  }, [
    abortVerification,
    navigate,
    onBackToBusinessDetails,
    patchApplicationProgressAsync,
    stage,
  ]);

  const handleCancelVerification = useCallback((): void => {
    abortVerification();
  }, [abortVerification]);

  const handleContinue = useCallback(async (): Promise<void> => {
    if (stage === 'select-id-type') {
      if (selectedIdentityDocumentType) {
        if (
          selectedIdentityDocumentType !==
          loadedFilesidentityDocumentTypeRef.current
        ) {
          setLoadedFiles([]);
          verificationCacheRef.current = null;
        }
        loadedFilesidentityDocumentTypeRef.current =
          selectedIdentityDocumentType;
        setStage('upload');
      }
      return;
    }

    if (stage === 'confirm-form') {
      logEvent(PageAction.merchantOnboardingUAConfirmDirectorDetails, {
        merchantApplicationId,
        selectedIdentityDocumentType,
      });
      setSubmitAttempted(true);

      const errors = selectedIdentityDocumentType
        ? getConfirmFormErrors(confirmFormValues, selectedIdentityDocumentType)
        : {};

      if (Object.keys(errors).length > 0) {
        return;
      }

      setIsSubmittingConfirmForm(true);

      try {
        const patchPayload = buildDirectorDetailsPatchPayload(
          confirmFormValues,
          selectedIdentityDocumentType!,
          residentialAddressRef.current
        );
        await patchMerchantApplicationAsync(patchPayload);
        await patchApplicationProgressAsync({
          directorDetails: ActivationProgressStatus.Confirmed,
        });
        if (isNdpReferrer) {
          await submitMerchantApplicationAsync();
          await refreshActivationData();
        }
        if (onReturnToDashboard) {
          await onReturnToDashboard();
        } else {
          navigate(PageRoute.Application);
        }
      } catch (err) {
        if (!isAbortError(err)) {
          Snackbar.error(
            'An error occurred submitting your details. Please try again.'
          );
        }
      } finally {
        setIsSubmittingConfirmForm(false);
      }
      return;
    }

    if (loadedFiles.length === 0) {
      return;
    }

    const runId = verificationRunIdRef.current + 1;
    verificationRunIdRef.current = runId;
    setIsVerifying(true);

    try {
      const results = await uploaderRef.current?.verify();

      if (verificationRunIdRef.current !== runId) {
        return;
      }

      if (results?.every(Boolean)) {
        setStage(
          skipConfirmLoadingRef.current ? 'confirm-form' : 'confirm-loading'
        );
        skipConfirmLoadingRef.current = false;
      }
    } finally {
      if (verificationRunIdRef.current === runId) {
        setIsVerifying(false);
      }
      if (verificationRunIdRef.current !== runId) {
        skipConfirmLoadingRef.current = false;
      }
      verificationAbortControllerRef.current = null;
    }
  }, [
    confirmFormValues,
    isNdpReferrer,
    loadedFiles.length,
    navigate,
    onReturnToDashboard,
    patchApplicationProgressAsync,
    patchMerchantApplicationAsync,
    refreshActivationData,
    selectedIdentityDocumentType,
    Snackbar,
    stage,
    submitMerchantApplicationAsync,
  ]);

  const handleConfirmTextFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      const { name, value } = event.target;

      setConfirmFormValues((currentValues) => ({
        ...currentValues,
        [name]: value,
      }));
    },
    []
  );

  const handleConfirmSelectChange = useCallback(
    (event: SelectChangeEvent<unknown>): void => {
      const { name, value } = event.target;

      setConfirmFormValues((currentValues) => ({
        ...currentValues,
        [name]: String(value ?? ''),
      }));
    },
    []
  );

  const handleDateOfBirthChange = useCallback(
    (value: moment.Moment | null): void => {
      setConfirmFormValues((curr) => ({
        ...curr,
        dateOfBirth: value ? value.format('YYYY-MM-DD') : '',
      }));
    },
    []
  );

  const handleAddressChange = useCallback(
    (_e: unknown, value: InlineAddress | null | undefined): void => {
      if (value === null) {
        // null = Google Places auto-resolution failed/returned nothing
        setResidentialAddressResolutionFailed(true);
        setRawAISuggestedAddress(null);
        return;
      }

      // Any structured address (complete or partial) = replace whatever we had
      // The manual-entry panel opens itself for the partial case
      // The field is populated for the happy path
      setResidentialAddressResolutionFailed(false);
      setRawAISuggestedAddress(null);

      residentialAddressRef.current = value ?? null;
      setConfirmFormValues((curr) => ({
        ...curr,
        residentialAddress: formatAddress(value ?? null),
      }));
    },
    []
  );

  useEffect(
    () => (): void => {
      abortRequests();
      verificationAbortControllerRef.current?.abort();
    },
    [abortRequests]
  );

  useEffect(() => {
    const directorDetails = activationData?.directorDetails;

    if (
      shouldStartAtConfirmFormRef.current &&
      !startedAtConfirmFormRef.current
    ) {
      const identityDocumentType = deriveIdentityDocumentType(directorDetails);
      if (identityDocumentType !== null) {
        startedAtConfirmFormRef.current = true;
        setSelectedIdentityDocumentType(identityDocumentType);
        setStage('confirm-form');
      } else if (!fetchForJumpRef.current) {
        fetchForJumpRef.current = true;
        hasAppliedCachePrefillRef.current = true; // prevent stale prefill while fetch is in flight
        getActivationData()
          .then((freshData) => {
            const freshDirectorDetails = freshData?.directorDetails;
            const freshIdentityDocumentType =
              deriveIdentityDocumentType(freshDirectorDetails);
            if (freshIdentityDocumentType !== null) {
              startedAtConfirmFormRef.current = true;
              setSelectedIdentityDocumentType(freshIdentityDocumentType);
              setStage('confirm-form');
            }
            if (freshDirectorDetails?.firstName) {
              setConfirmFormValues(
                getInitialConfirmFormValues(freshDirectorDetails)
              );
              if (freshDirectorDetails.residentialAddress) {
                residentialAddressRef.current =
                  freshDirectorDetails.residentialAddress;
              }
            }
          })
          .catch(() => {});
      }
    }

    // In the edit flow, fetch fresh API data for prefill to get the latest saved values
    if (
      shouldStartAtConfirmFormRef.current &&
      !hasAppliedCachePrefillRef.current
    ) {
      hasAppliedCachePrefillRef.current = true;
      getActivationData()
        .then((freshData) => {
          const freshDirectorDetails = freshData?.directorDetails;
          if (freshDirectorDetails?.firstName) {
            setConfirmFormValues(
              getInitialConfirmFormValues(freshDirectorDetails)
            );
            if (freshDirectorDetails.residentialAddress) {
              residentialAddressRef.current =
                freshDirectorDetails.residentialAddress;
            }
          }
        })
        .catch(() => {});
      return;
    }

    if (!hasAppliedCachePrefillRef.current && directorDetails?.firstName) {
      hasAppliedCachePrefillRef.current = true;
      setConfirmFormValues(getInitialConfirmFormValues(directorDetails));
      if (directorDetails.residentialAddress) {
        residentialAddressRef.current = directorDetails.residentialAddress;
      }
    }
  }, [activationData, getActivationData]);

  useEffect(() => {
    if (stage === 'select-id-type') {
      logEvent(PageAction.merchantOnboardingPVDirectorDetails, {
        merchantApplicationId,
      });
    } else if (stage === 'upload') {
      logEvent(PageAction.merchantOnboardingPVUploadDirectorDetails, {
        merchantApplicationId,
        documentType: selectedIdentityDocumentType,
      });
    } else if (stage === 'confirm-form') {
      logEvent(PageAction.merchantOnboardingPVConfirmDirectorDetails, {
        merchantApplicationId,
        documentType: selectedIdentityDocumentType,
      });
    }
  }, [stage]);

  useEffect(() => {
    if (stage !== 'confirm-loading') {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setStage('confirm-form');
    }, CONFIRM_DETAILS_LOADING_MS);

    return (): void => {
      window.clearTimeout(timeoutId);
    };
  }, [stage]);

  const passportCountryOptions = useMemo(() => {
    const countries = formOptions?.['Countries'];
    if (countries?.length) {
      return countries.map((value) => ({ value, label: value }));
    }
    return [];
  }, [formOptions]);

  const confirmSelectOptions = useMemo(
    () =>
      getSelectOptions(
        selectedIdentityDocumentType,
        selectedIdentityDocumentType === IdentityDocumentType.DriversLicence
          ? confirmFormValues.state
          : confirmFormValues.country,
        passportCountryOptions
      ).map((option) => ({
        value: option.value,
        label: option.label,
      })),
    [
      confirmFormValues.state,
      confirmFormValues.country,
      selectedIdentityDocumentType,
      passportCountryOptions,
    ]
  );

  const confirmFormErrors = useMemo<ConfirmFormErrors>(
    () =>
      selectedIdentityDocumentType
        ? getConfirmFormErrors(confirmFormValues, selectedIdentityDocumentType)
        : {},
    [confirmFormValues, selectedIdentityDocumentType]
  );

  useEffect(() => {
    if (stage !== 'confirm-form') {
      setSubmitAttempted(false);
    }
  }, [stage]);

  // True when AI has prefilled the display string but structured fields are still empty
  // triggers auto-resolution in AddressLookupInline
  const isResidentialAddressPendingResolution = rawAISuggestedAddress !== null;

  const fieldErr = (field: keyof DirectorConfirmFormValues): string =>
    submitAttempted ? (confirmFormErrors[field] ?? '') : '';

  const buildDirectorProgressPayload = useCallback(
    (directorDetails: ActivationProgressStatus) => {
      const storedActivationData = getStoredActivationData();

      return {
        directorDetails,
        ...(storedActivationData?.businessDetailsStepAAIConfirmed != null && {
          businessDetailsStepAAIConfirmed:
            storedActivationData.businessDetailsStepAAIConfirmed,
        }),
        ...(storedActivationData?.businessDetailsStepBAIConfirmed != null && {
          businessDetailsStepBAIConfirmed:
            storedActivationData.businessDetailsStepBAIConfirmed,
        }),
      };
    },
    []
  );

  const handleCloseToDashboard = useCallback(async (): Promise<void> => {
    abortVerification();

    try {
      if (stage === 'confirm-form' && selectedIdentityDocumentType) {
        const errors = getConfirmFormErrors(
          confirmFormValues,
          selectedIdentityDocumentType
        );

        if (Object.keys(errors).length === 0) {
          const patchPayload = buildDirectorDetailsPatchPayload(
            confirmFormValues,
            selectedIdentityDocumentType,
            residentialAddressRef.current
          );
          await patchMerchantApplicationAsync(patchPayload);
        }
      }

      const progressStatus =
        stage === 'select-id-type'
          ? ActivationProgressStatus.Upload
          : ActivationProgressStatus.Edit;

      await patchApplicationProgressAsync(
        buildDirectorProgressPayload(progressStatus)
      );

      if (onReturnToDashboard) {
        await onReturnToDashboard();
      } else {
        navigate(PageRoute.Application);
      }
    } catch (error) {
      if (!isAbortError(error)) {
        Snackbar.error(
          'An error occurred saving your progress. Please try again.'
        );
      }
    }
  }, [
    abortVerification,
    buildDirectorProgressPayload,
    confirmFormValues,
    navigate,
    onReturnToDashboard,
    patchApplicationProgressAsync,
    patchMerchantApplicationAsync,
    selectedIdentityDocumentType,
    Snackbar,
    stage,
  ]);

  useEffect(() => {
    onRegisterCloseHandler?.(handleCloseToDashboard);

    return (): void => {
      onRegisterCloseHandler?.(null);
    };
  }, [handleCloseToDashboard, onRegisterCloseHandler]);

  return (
    <ThemeProvider theme={theme}>
      <div
        css={[
          styles.page,
          (stage === 'confirm-loading' || stage === 'confirm-form') &&
            styles.confirmPage,
        ]}
      >
        <div css={styles.panel}>
          {stage === 'select-id-type' && (
            <>
              <div css={styles.section}>
                <h1 className="heading1" css={styles.title}>
                  Verify director&apos;s ID
                </h1>
                <div css={styles.progressTrack}>
                  <div css={styles.progressFill(progressPercent)} />
                </div>
              </div>

              <div css={styles.selectIdentityDocumentTypeContent}>
                <div css={styles.intro}>
                  <p className="body1" css={styles.introTitle}>
                    Choose ID type
                  </p>
                  <p className="body2" css={styles.introBody}>
                    You must only upload identification of a company director or
                    authorised representative.
                  </p>
                </div>

                <div css={styles.optionList}>
                  <button
                    type="button"
                    css={styles.optionCard(
                      selectedIdentityDocumentType ===
                        IdentityDocumentType.DriversLicence
                    )}
                    onClick={(): void =>
                      setSelectedIdentityDocumentType(
                        IdentityDocumentType.DriversLicence
                      )
                    }
                    aria-pressed={
                      selectedIdentityDocumentType ===
                      IdentityDocumentType.DriversLicence
                    }
                  >
                    <span css={styles.optionIcon}>
                      <Icons.UserId width={24} height={24} />
                    </span>
                    <span css={styles.optionCopy}>
                      <span className="body1" css={styles.optionTitle}>
                        Driver licence
                      </span>
                      <span className="body4" css={styles.optionDescription}>
                        Upload photo or scan of front and back
                      </span>
                    </span>
                  </button>

                  <button
                    type="button"
                    css={styles.optionCard(
                      selectedIdentityDocumentType ===
                        IdentityDocumentType.Passport
                    )}
                    onClick={(): void =>
                      setSelectedIdentityDocumentType(
                        IdentityDocumentType.Passport
                      )
                    }
                    aria-pressed={
                      selectedIdentityDocumentType ===
                      IdentityDocumentType.Passport
                    }
                  >
                    <span css={styles.optionIcon}>
                      <Icons.Globe width={24} height={24} />
                    </span>
                    <span css={styles.optionCopy}>
                      <span className="body1" css={styles.optionTitle}>
                        Passport
                      </span>
                      <span className="body4" css={styles.optionDescription}>
                        Upload photo or scan of passport page
                      </span>
                    </span>
                  </button>
                </div>
              </div>

              <div css={styles.actionRow}>
                <NavButtons
                  canGoBack
                  canContinue={canContinue}
                  isSubmitting={false}
                  onBack={handleBack}
                  onContinue={handleContinue}
                  continueLabel={continueLabel}
                />
              </div>
            </>
          )}

          {stage === 'upload' && selectedContent && (
            <>
              <div css={styles.uploadContentGroup}>
                <div css={styles.section}>
                  <h1 className="heading1" css={styles.title}>
                    {selectedContent.title}
                  </h1>
                  <div css={styles.progressTrack}>
                    <div css={styles.progressFill(progressPercent)} />
                  </div>
                </div>

                <div css={styles.infoBanner}>
                  <span css={styles.infoIcon}>
                    <Icons.InformationCircle width={16} height={16} />
                  </span>
                  <p className="body4" css={styles.infoCopy}>
                    {selectedContent.infoMessage}
                  </p>
                </div>

                <div css={styles.uploaderWrapper}>
                  <GuidedFileUploader
                    ref={uploaderRef}
                    requirements={selectedContent.requirements}
                    onVerify={handleUploaderVerify}
                    onFilesChange={handleFilesChange}
                    multiple={selectedContent.multiple}
                    onSingleFileReplaceAttempt={
                      selectedIdentityDocumentType ===
                      IdentityDocumentType.Passport
                        ? handlePassportSingleFileReplaceAttempt
                        : undefined
                    }
                    maxFiles={
                      selectedIdentityDocumentType ===
                      IdentityDocumentType.DriversLicence
                        ? MAX_DRIVERS_LICENCE_FILES
                        : undefined
                    }
                    onMaxFilesAttempt={
                      selectedIdentityDocumentType ===
                      IdentityDocumentType.DriversLicence
                        ? handleDriversLicenceMaxFilesAttempt
                        : undefined
                    }
                    initialFiles={loadedFiles}
                    initialRequirementResults={
                      verificationCacheRef.current !== null &&
                      verificationCacheRef.current.identityDocumentType ===
                        selectedIdentityDocumentType &&
                      loadedFiles.length ===
                        verificationCacheRef.current.files.length &&
                      loadedFiles.every(
                        (f, i) => f === verificationCacheRef.current!.files[i]
                      )
                        ? verificationCacheRef.current.results
                        : null
                    }
                  />
                </div>
                {uploadErrorMessage && (
                  <div css={styles.errorBanner}>
                    <span css={styles.errorIcon}>
                      <Icons.WarningCircle
                        width={16}
                        height={16}
                        fill={theme.colors.danger}
                      />
                    </span>
                    <p className="body4" css={styles.errorCopy}>
                      {uploadErrorMessage}
                    </p>
                  </div>
                )}
              </div>

              <div css={styles.actionRow}>
                <NavButtons
                  canGoBack
                  canContinue={canContinue}
                  isSubmitting={false}
                  onBack={handleBack}
                  onContinue={handleContinue}
                  continueLabel={continueLabel}
                />
              </div>
            </>
          )}

          {stage === 'confirm-loading' && (
            <>
              <div css={[styles.section, styles.confirmHeader]}>
                <h1 className="heading1" css={styles.title}>
                  Confirm director&apos;s details
                </h1>
                <div css={styles.progressTrack}>
                  <div css={styles.progressFill(progressPercent)} />
                </div>
              </div>

              <div css={styles.successSection}>
                <div css={styles.successSkeleton(250)} />
                <div css={styles.successSkeleton(182)} />
                <div css={styles.successSkeleton(63)} />
              </div>
            </>
          )}

          {stage === 'confirm-form' && selectedIdentityDocumentType && (
            <>
              <div css={[styles.section, styles.confirmHeader]}>
                <h1 className="heading1" css={styles.title}>
                  Confirm director&apos;s details
                </h1>
                <div css={styles.progressTrack}>
                  <div css={styles.progressFill(progressPercent)} />
                </div>
              </div>

              <div css={styles.confirmForm}>
                <div css={styles.formSection}>
                  <div css={styles.formSectionHeader}>
                    <p className="body1" css={styles.formSectionTitle}>
                      {selectedIdentityDocumentType ===
                      IdentityDocumentType.DriversLicence
                        ? 'Driver licence details'
                        : 'Passport details'}
                    </p>
                  </div>

                  <div css={styles.formStack}>
                    {selectedIdentityDocumentType ===
                    IdentityDocumentType.DriversLicence ? (
                      <div css={styles.formRow}>
                        <TextFields.Select
                          name="state"
                          label="State *"
                          css={styles.textfield}
                          options={confirmSelectOptions}
                          error={Boolean(fieldErr('state'))}
                          helperText={renderFieldError(
                            Boolean(fieldErr('state')),
                            fieldErr('state')
                          )}
                          slotProps={{
                            select: {
                              onChange: handleConfirmSelectChange,
                              value: confirmFormValues.state,
                            },
                            formHelperText: { className: 'body4' },
                          }}
                        />

                        <TextFields.Outlined
                          type="text"
                          name="licenceNumber"
                          label="Licence number *"
                          css={styles.textfield}
                          className="fs-mask"
                          value={confirmFormValues.licenceNumber}
                          onChange={handleConfirmTextFieldChange}
                          error={Boolean(fieldErr('licenceNumber'))}
                          helperText={renderFieldError(
                            Boolean(fieldErr('licenceNumber')),
                            fieldErr('licenceNumber')
                          )}
                          slotProps={{
                            formHelperText: { className: 'body4' },
                          }}
                        />
                      </div>
                    ) : (
                      <>
                        <TextFields.Select
                          name="country"
                          label="Country *"
                          css={styles.textfield}
                          options={confirmSelectOptions}
                          error={Boolean(fieldErr('country'))}
                          helperText={renderFieldError(
                            Boolean(fieldErr('country')),
                            fieldErr('country')
                          )}
                          slotProps={{
                            select: {
                              onChange: handleConfirmSelectChange,
                              value: confirmFormValues.country,
                            },
                            formHelperText: { className: 'body4' },
                          }}
                        />

                        <TextFields.Outlined
                          type="text"
                          name="passportNumber"
                          label="Passport number *"
                          css={styles.textfield}
                          className="fs-mask"
                          value={confirmFormValues.passportNumber}
                          onChange={handleConfirmTextFieldChange}
                          error={Boolean(fieldErr('passportNumber'))}
                          helperText={renderFieldError(
                            Boolean(fieldErr('passportNumber')),
                            fieldErr('passportNumber')
                          )}
                          slotProps={{
                            formHelperText: { className: 'body4' },
                          }}
                        />
                      </>
                    )}

                    {selectedIdentityDocumentType ===
                      IdentityDocumentType.DriversLicence && (
                      <TextFields.Outlined
                        type="text"
                        name="licenceCardNumber"
                        label="Licence card number *"
                        css={styles.textfield}
                        className="fs-mask"
                        value={confirmFormValues.licenceCardNumber}
                        onChange={handleConfirmTextFieldChange}
                        error={Boolean(fieldErr('licenceCardNumber'))}
                        helperText={renderFieldError(
                          Boolean(fieldErr('licenceCardNumber')),
                          fieldErr('licenceCardNumber')
                        )}
                        slotProps={{
                          formHelperText: { className: 'body4' },
                        }}
                      />
                    )}
                  </div>
                </div>

                <div css={styles.formSection}>
                  <div css={styles.formSectionHeader}>
                    <p className="body1" css={styles.formSectionTitle}>
                      Director&apos;s personal details
                    </p>
                  </div>

                  <div css={styles.formStack}>
                    <TextFields.Outlined
                      type="text"
                      name="firstName"
                      label="First name *"
                      css={styles.textfield}
                      className="fs-mask"
                      value={confirmFormValues.firstName}
                      onChange={handleConfirmTextFieldChange}
                      error={Boolean(fieldErr('firstName'))}
                      helperText={renderFieldError(
                        Boolean(fieldErr('firstName')),
                        fieldErr('firstName')
                      )}
                      slotProps={{
                        formHelperText: { className: 'body4' },
                      }}
                    />
                    <TextFields.Outlined
                      type="text"
                      name="middleName"
                      label="Middle name"
                      css={styles.textfield}
                      className="fs-mask"
                      value={confirmFormValues.middleName}
                      onChange={handleConfirmTextFieldChange}
                      slotProps={{
                        formHelperText: { className: 'body4' },
                      }}
                    />
                    <TextFields.Outlined
                      type="text"
                      name="lastName"
                      label="Last name *"
                      css={styles.textfield}
                      className="fs-mask"
                      value={confirmFormValues.lastName}
                      onChange={handleConfirmTextFieldChange}
                      error={Boolean(fieldErr('lastName'))}
                      helperText={renderFieldError(
                        Boolean(fieldErr('lastName')),
                        fieldErr('lastName')
                      )}
                      slotProps={{
                        formHelperText: { className: 'body4' },
                      }}
                    />
                    <TextFields.Date
                      label="Date of birth *"
                      css={styles.textfield}
                      className="fs-mask"
                      value={
                        confirmFormValues.dateOfBirth
                          ? moment(confirmFormValues.dateOfBirth, 'YYYY-MM-DD')
                          : null
                      }
                      onChange={handleDateOfBirthChange}
                      disableFuture
                      views={['year', 'month', 'day']}
                      slotProps={{
                        openPickerButton: {
                          'aria-label': 'open calendar',
                        },
                        textField: {
                          placeholder: 'DD/MM/YYYY',
                          fullWidth: true,
                          error: Boolean(fieldErr('dateOfBirth')),
                          helperText: renderFieldError(
                            Boolean(fieldErr('dateOfBirth')),
                            fieldErr('dateOfBirth')
                          ),
                        },
                      }}
                    />
                    <div className="fs-mask" css={styles.textfield}>
                      <AddressLookupInline
                        name="residentialAddress"
                        label="Residential address *"
                        value={
                          isResidentialAddressPendingResolution
                            ? (rawAISuggestedAddress ?? '')
                            : confirmFormValues.residentialAddress
                        }
                        error={
                          Boolean(fieldErr('residentialAddress')) ||
                          residentialAddressResolutionFailed
                        }
                        helperText={
                          fieldErr('residentialAddress') ||
                          (residentialAddressResolutionFailed
                            ? 'Please search and select your address from the dropdown'
                            : '')
                        }
                        handleChange={handleAddressChange}
                        manualAddress={
                          isResidentialAddressPendingResolution
                            ? undefined
                            : residentialAddressRef.current
                        }
                        autoResolve={isResidentialAddressPendingResolution}
                      />
                    </div>
                  </div>
                </div>

                <div css={[styles.formSection, css.marginTop(16)]}>
                  <div css={styles.formSectionHeader}>
                    <p className="body1" css={styles.formSectionTitle}>
                      Director&apos;s contact details
                    </p>
                  </div>

                  <div css={styles.formStack}>
                    <TextFields.Outlined
                      type="tel"
                      name="contactMobile"
                      label="Mobile number *"
                      css={styles.textfield}
                      className="fs-mask"
                      value={confirmFormValues.contactMobile}
                      onChange={handleConfirmTextFieldChange}
                      error={Boolean(fieldErr('contactMobile'))}
                      helperText={renderFieldError(
                        Boolean(fieldErr('contactMobile')),
                        fieldErr('contactMobile')
                      )}
                      slotProps={{
                        formHelperText: { className: 'body4' },
                      }}
                    />
                    <TextFields.Outlined
                      type="email"
                      name="contactEmail"
                      label="Email *"
                      css={styles.textfield}
                      className="fs-mask"
                      value={confirmFormValues.contactEmail}
                      onChange={handleConfirmTextFieldChange}
                      error={Boolean(fieldErr('contactEmail'))}
                      helperText={renderFieldError(
                        Boolean(fieldErr('contactEmail')),
                        fieldErr('contactEmail')
                      )}
                      slotProps={{
                        formHelperText: { className: 'body4' },
                      }}
                    />
                  </div>
                </div>
              </div>

              <div css={styles.actionRow}>
                <NavButtons
                  canGoBack
                  canContinue={canContinue}
                  isSubmitting={isSubmittingConfirmForm}
                  onBack={handleBack}
                  onContinue={handleContinue}
                  continueLabel={continueLabel}
                />
              </div>
            </>
          )}
        </div>

        {isVerifying && (
          <div css={styles.overlay}>
            <div css={styles.verificationCard}>
              <div css={styles.verificationIllustration} aria-hidden="true">
                <img
                  src={VerifyIdIllustration}
                  alt=""
                  css={styles.verificationIllustrationImage}
                />
              </div>

              <div css={styles.verificationContent}>
                <p className="heading2" css={styles.verificationTitle}>
                  Verifying director&apos;s identity
                </p>

                <div css={styles.verificationTrack}>
                  <div css={styles.verificationFill} />
                </div>

                <Buttons.Text
                  onClick={handleCancelVerification}
                  css={styles.cancelButton}
                >
                  Cancel
                </Buttons.Text>
              </div>
            </div>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
};

export default DirectorDetailsStepModule;
